import type { FeatureLike } from 'ol/Feature';

import type { ProductCompanyLocation } from '../../../types';
import type { ProductMarkerBucket } from '../views/ProductMapDrawer';
import type { ProductLocationCollector } from '../types';

const sanitizeProductName = (value?: string | null): string => (value ?? '').trim().toLowerCase();

export const computeLocationUniqueProductCount = (location: ProductCompanyLocation): number => {
  if (!Array.isArray(location.products)) {
    return 0;
  }

  const uniqueNames = new Set<string>();
  location.products.forEach((product) => {
    const name = sanitizeProductName(product.product_name);
    if (name.length > 0) {
      uniqueNames.add(name);
    }
  });

  return uniqueNames.size;
};

export const buildProductLocationKey = (location: ProductCompanyLocation): string => {
  const user = location.userid !== undefined && location.userid !== null ? String(location.userid) : 'undefined';
  const company = location.company ?? '';
  const lat = Number.isFinite(location.latitude) ? String(location.latitude) : 'lat-na';
  const lon = Number.isFinite(location.longitude) ? String(location.longitude) : 'lon-na';

  return `${user}-${company}-${lat}-${lon}`;
};

export const parseNumericAmount = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return 0;
    if (/m$/i.test(trimmed)) return parseFloat(trimmed) * 1_000_000;
    if (/k$/i.test(trimmed)) return parseFloat(trimmed) * 1_000;
    const numeric = parseFloat(trimmed.replace(/[^0-9.-]+/g, ''));
    return Number.isFinite(numeric) ? numeric : 0;
  }
  if (typeof value === 'bigint') {
    return Number(value);
  }
  return 0;
};

export const computeProductDrawerTitle = (locations: ProductCompanyLocation[]): string => {
  if (locations.length === 0) {
    return 'Product Insights';
  }

  if (locations.length === 1) {
    const single = locations[0];
    const locationLabel = [single.city, single.state, single.country].filter(Boolean).join(', ');
    return [single.company || 'Company', locationLabel].filter(Boolean).join(' | ');
  }

  const uniqueCityKeys = new Set(
    locations.map((loc) => `${(loc.city || '').toLowerCase()}|${(loc.state || '').toLowerCase()}|${(loc.country || '').toLowerCase()}`),
  );

  if (uniqueCityKeys.size === 1) {
    const sample = locations[0];
    const locationLabel = [sample.city, sample.state, sample.country].filter(Boolean).join(', ');
    const locationSuffix = locationLabel ? ` | ${locationLabel}` : '';
    return `${locations.length} Companies${locationSuffix}`;
  }

  return `${locations.length} Companies`;
};

export const collectProductLocationIntoMap = (
  feature: FeatureLike | undefined,
  collector: ProductLocationCollector,
) => {
  if (!feature) return;
  const anyFeature = feature as any;
  if (typeof anyFeature.get !== 'function') return;

  const directLocation = anyFeature.get('productLocation') as ProductCompanyLocation | undefined;
  if (directLocation) {
    const key = buildProductLocationKey(directLocation);
    if (!collector.has(key)) {
      collector.set(key, directLocation);
    }
  }

  const props = anyFeature.get('properties');
  if (props?.productLocation) {
    const location = props.productLocation as ProductCompanyLocation;
    const key = buildProductLocationKey(location);
    if (!collector.has(key)) {
      collector.set(key, location);
    }
  }
};

const PRODUCT_BUCKET_LABELS = {
  low: 'Cross-sell opportunity',
  mid: 'Growth account',
  high: 'Strategic account',
};

export const DEFAULT_PRODUCT_BUCKETS: ProductMarkerBucket[] = [
  { key: 'bucket-low-default', color: '#22c55e', label: PRODUCT_BUCKET_LABELS.low, min: 0, max: 3 },
  { key: 'bucket-mid-default', color: '#f59e0b', label: PRODUCT_BUCKET_LABELS.mid, min: 4, max: 7 },
  { key: 'bucket-high-default', color: '#ef4444', label: PRODUCT_BUCKET_LABELS.high, min: 8, max: Number.POSITIVE_INFINITY },
];

export const deriveProductMarkerBuckets = (counts: number[]): ProductMarkerBucket[] => {
  const sanitized = counts
    .map((value) => (Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0))
    .filter((value) => value >= 0);

  if (sanitized.length === 0) {
    return DEFAULT_PRODUCT_BUCKETS;
  }

  const minValue = sanitized.reduce((min, value) => Math.min(min, value), sanitized[0]);
  const maxValue = sanitized.reduce((max, value) => Math.max(max, value), sanitized[0]);

  if (minValue === maxValue) {
    const base = minValue;
    return [
      {
        key: `bucket-low-${base}`,
        color: '#22c55e',
        min: 0,
        max: base,
        label: PRODUCT_BUCKET_LABELS.low,
      },
      {
        key: `bucket-mid-${base + 1}`,
        color: '#f59e0b',
        min: base + 1,
        max: base + 1,
        label: PRODUCT_BUCKET_LABELS.mid,
      },
      {
        key: `bucket-high-${base + 2}`,
        color: '#ef4444',
        min: base + 2,
        max: Number.POSITIVE_INFINITY,
        label: PRODUCT_BUCKET_LABELS.high,
      },
    ];
  }

  const span = maxValue - minValue + 1;
  const step = Math.max(1, Math.ceil(span / 3));
  const firstMax = Math.min(maxValue, minValue + step - 1);
  const secondMax = Math.min(maxValue, firstMax + step);

  const buckets: ProductMarkerBucket[] = [
    {
      key: `bucket-low-${firstMax}`,
      color: '#22c55e',
      min: minValue,
      max: firstMax,
      label: PRODUCT_BUCKET_LABELS.low,
    },
    {
      key: `bucket-mid-${secondMax}`,
      color: '#f59e0b',
      min: firstMax + 1,
      max: secondMax,
      label: PRODUCT_BUCKET_LABELS.mid,
    },
    {
      key: `bucket-high-${secondMax + 1}`,
      color: '#ef4444',
      min: secondMax + 1,
      max: Number.POSITIVE_INFINITY,
      label: PRODUCT_BUCKET_LABELS.high,
    },
  ];

  return buckets;
};

export const selectProductMarkerBucket = (buckets: ProductMarkerBucket[], rawCount: number): ProductMarkerBucket => {
  const count = Number.isFinite(rawCount) ? Math.max(0, Math.round(rawCount)) : 0;

  for (const bucket of buckets) {
    if (count <= bucket.max) {
      return bucket;
    }
  }

  return buckets[buckets.length - 1] ?? DEFAULT_PRODUCT_BUCKETS[DEFAULT_PRODUCT_BUCKETS.length - 1];
};


